// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// ManualApiError. Activated by setting VITE_USE_MOCK_API=false; see
// counseling-manual.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  ManualApiError,
  type ConsultationManualApi,
  type ConsultationManualDetail,
  type CreateManualInput,
  type ManualApiErrorCode,
  type ManualCategoryTree,
  type ManualListPage,
  type ManualListParams,
  type UpdateManualInput,
  type UploadResult,
} from '../types/counseling-manual.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new ManualApiError(
    (payload?.code as ManualApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

const base = endpoints.consultationManuals;
const categories = `${base}/categories`;
// SHCT-454 — image-block upload endpoint (multipart). BE to add
// `POST ${consultationManuals}/uploads` reusing the shared BlockMediaService
// (same as Health Encyclopedia's `/uploads` and Management Guidelines').
const uploadsPath = `${base}/uploads`;

export const counselingManualHttpApi: ConsultationManualApi = {
  async list(params: ManualListParams): Promise<ManualListPage> {
    try {
      // Only send non-empty params so the URL stays clean.
      const query: Record<string, string | number> = { page: params.page, size: params.size };
      if (params.area) query.area = params.area;
      if (params.subArea) query.subArea = params.subArea;
      if (params.keyword) query.keyword = params.keyword;
      if (params.sort) query.sort = params.sort;
      if (params.direction) query.direction = params.direction;
      const res = await httpClient.get<ApiEnvelope<ManualListPage>>(base, { params: query });
      const data = res.data?.data;
      // The table iterates `items`; a wrong shape (non-paged body, HTML, error
      // envelope) must surface as a load error, not white-screen the table.
      if (!data || !Array.isArray(data.items)) {
        throw new ManualApiError('UNKNOWN', '목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async get(cid: string): Promise<ConsultationManualDetail> {
    try {
      const res = await httpClient.get<ApiEnvelope<ConsultationManualDetail>>(`${base}/${cid}`);
      const data = res.data?.data;
      // `blocks` hydrates the block editor; a present-but-non-array value would
      // white-screen the editor on iteration.
      if (!data || !Array.isArray(data.blocks)) {
        throw new ManualApiError('UNKNOWN', '상담 매뉴얼을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async create(input: CreateManualInput): Promise<ConsultationManualDetail> {
    try {
      const res = await httpClient.post<ApiEnvelope<ConsultationManualDetail>>(base, input);
      const data = res.data?.data;
      // `blocks` hydrates the block editor; a present-but-non-array value would
      // white-screen the editor on iteration.
      if (!data || !Array.isArray(data.blocks)) {
        throw new ManualApiError('UNKNOWN', '상담 매뉴얼을 저장할 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async update(cid: string, input: UpdateManualInput): Promise<ConsultationManualDetail> {
    try {
      const res = await httpClient.put<ApiEnvelope<ConsultationManualDetail>>(`${base}/${cid}`, input);
      const data = res.data?.data;
      // `blocks` hydrates the block editor; a present-but-non-array value would
      // white-screen the editor on iteration.
      if (!data || !Array.isArray(data.blocks)) {
        throw new ManualApiError('UNKNOWN', '상담 매뉴얼을 저장할 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async remove(cid: string): Promise<void> {
    try {
      await httpClient.delete(`${base}/${cid}`);
    } catch (e) {
      mapError(e);
    }
  },

  // Read-only tree, sourced from PRO Item Design (SSoT). Classification
  // Management (add/edit/delete) was removed — ADM.019/020, PRD 11 v1.2.
  async getCategoryTree(): Promise<ManualCategoryTree> {
    try {
      const res = await httpClient.get<ApiEnvelope<ManualCategoryTree>>(categories);
      const data = res.data?.data;
      // `areas` drives the category tree UI; a wrong shape must surface as a
      // load error rather than crash the tree walkers.
      if (!data || !Array.isArray(data.areas)) {
        throw new ManualApiError('UNKNOWN', '카테고리를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  // SHCT-454 — upload one image file via multipart; the block stores the
  // returned URL (not base64), so the manual save body stays small.
  async uploadImage(file: File): Promise<UploadResult> {
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await httpClient.post<ApiEnvelope<UploadResult>>(uploadsPath, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data;
      if (!data || typeof data.url !== 'string') {
        throw new ManualApiError('UNKNOWN', '이미지 업로드에 실패했습니다. 파일 형식과 크기를 확인해 주세요.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },
};
